import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingCompletionFlow from '../components/BookingCompletionFlow';
import { AcceptQuoteModal, JobsList, JobChatModal, JobBidsModal } from '../components/dashboard';
import { 
  SimpleBookingDetailsModal, 
  ProviderBidsTab, 
  JobDetailsModal, 
  BookingsListView 
} from '../components/bookings';
import BookingReasonModal from '../components/bookings/BookingReasonModal';
import { useFees } from '../context/FeeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyBookings = ({ user, onLogout, onUserUpdate }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { calculateProviderPayout } = useFees();
  const [bookings, setBookings] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showAcceptQuoteModal, setShowAcceptQuoteModal] = useState(false);
  const [selectedBookingForAccept, setSelectedBookingForAccept] = useState(null);
  const [selectedJobForBids, setSelectedJobForBids] = useState(null);
  const [jobBids, setJobBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(false);
  const [processingBid, setProcessingBid] = useState(null);
  const [bookingFilter, setBookingFilter] = useState('all');
  
  // Accept quote modal state
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [projectEndDate, setProjectEndDate] = useState('');
  const [projectEndTime, setProjectEndTime] = useState('17:00');
  const [blockCalendar, setBlockCalendar] = useState(false);
  
  // Reject-booking (provider) + cancel-with-note (client, price-adjusted) modals
  const [rejectTarget, setRejectTarget] = useState(null);
  const [submittingReject, setSubmittingReject] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [submittingCancel, setSubmittingCancel] = useState(false);
  
  // For providers: My Bids state
  const [myBids, setMyBids] = useState([]);
  const [showJobChatModal, setShowJobChatModal] = useState(false);
  const [selectedJobConversation, setSelectedJobConversation] = useState(null);
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  
  // For clients: show tabs for Bookings and Posted Jobs
  // For providers: show tabs for Bookings and My Bids
  const effectiveRole = user?.active_role || user?.role;
  const isClient = effectiveRole === 'client';
  const isProvider = effectiveRole === 'provider';
  // Default to 'my-bids' for providers, 'bookings' for clients
  const [activeTab, setActiveTab] = useState(isProvider ? 'my-bids' : 'bookings');
  const [activeDisputes, setActiveDisputes] = useState(0);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRole]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const currentIsClient = (user?.active_role || user?.role) === 'client';
      const currentIsProvider = (user?.active_role || user?.role) === 'provider';
      
      const promises = [
        axios.get(`${API}/bookings`, { headers }),
        currentIsClient ? axios.get(`${API}/jobs/my`, { headers }) : Promise.resolve({ data: [] }),
        currentIsProvider ? axios.get(`${API}/bids/my`, { headers }) : Promise.resolve({ data: [] })
      ];
      
      const [bookingsRes, jobsRes, bidsRes] = await Promise.all(promises);

      // Fetch badge counts for active disputes
      try {
        const badgeRes = await axios.get(`${API}/badge-counts`, { headers });
        setActiveDisputes(badgeRes.data?.active_disputes || 0);
      } catch (e) {
        console.error('Badge counts fetch error:', e);
      }
      setBookings(bookingsRes.data);
      setJobs(jobsRes.data);
      setMyBids(bidsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== Utility Functions ====================
  
  const formatBookingDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatBookingTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'accepted': return 'bg-blue-100 text-blue-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'cancelled':
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'completion_requested': return 'bg-purple-100 text-purple-700';
      case 'disputed': return 'bg-red-100 text-red-700 ring-1 ring-red-300';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // ==================== Bid Handlers (Provider) ====================
  
  const handleWithdrawBid = async (bidId) => {
    if (!window.confirm(t('myBookings.confirmWithdraw'))) return;
    try {
      await axios.delete(`${API}/bids/${bidId}`);
      fetchData();
    } catch (error) {
      console.error('Error withdrawing bid:', error);
      alert(error.response?.data?.detail || 'Failed to withdraw bid');
    }
  };

  const handleClearAllBids = async () => {
    if (!window.confirm(t('myBookings.confirmClearBids'))) return;
    try {
      const pendingBids = myBids.filter(bid => bid.status === 'pending');
      await Promise.all(pendingBids.map(bid => axios.delete(`${API}/bids/${bid.id}`)));
      fetchData();
    } catch (error) {
      console.error('Error clearing bids:', error);
      alert('Failed to clear some bids');
    }
  };

  const handleDeleteRejectedBid = async (bidId) => {
    if (!window.confirm(t('myBookings.confirmDeleteBid'))) return;
    try {
      await axios.delete(`${API}/bids/${bidId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting bid:', error);
      alert(error.response?.data?.detail || 'Failed to delete bid');
    }
  };

  const fetchJobBids = async (job) => {
    setSelectedJobForBids(job);
    setLoadingBids(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/jobs/${job.id}/bids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobBids(response.data);
      window.dispatchEvent(new Event('refreshBadgeCounts'));
    } catch (error) {
      console.error('Error fetching bids:', error);
      setJobBids([]);
    } finally {
      setLoadingBids(false);
    }
  };

  const handleAcceptBid = async (bidId) => {
    setProcessingBid(bidId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/bids/${bidId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedJobForBids) {
        await fetchJobBids(selectedJobForBids);
      }
      fetchData();
    } catch (error) {
      console.error('Error accepting bid:', error);
      alert(error.response?.data?.detail || 'Failed to accept bid');
    } finally {
      setProcessingBid(null);
    }
  };

  const handleRejectBid = async (bidId) => {
    setProcessingBid(bidId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/bids/${bidId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedJobForBids) {
        await fetchJobBids(selectedJobForBids);
      }
      fetchData();
    } catch (error) {
      console.error('Error rejecting bid:', error);
      alert(error.response?.data?.detail || 'Failed to reject bid');
    } finally {
      setProcessingBid(null);
    }
  };

  const handleMessageProviderFromBid = async (bid) => {
    try {
      const token = localStorage.getItem('token');
      const jobTitle = selectedJobForBids?.title || bid.job_title || 'Job Inquiry';
      
      const response = await axios.post(
        `${API}/bids/${bid.id}/inquiry`,
        { message: `Hello! I accepted your bid for "${jobTitle}". Let's discuss the details.` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.inquiry_id) {
        navigate(`/inquiries?conversation=${response.data.inquiry_id}`);
      } else {
        navigate(`/inquiries`);
      }
    } catch (error) {
      console.error('Error creating inquiry:', error);
      navigate(`/inquiries?provider=${bid.provider_id}`);
    }
  };

  const handleAskQuestion = async (bid) => {
    try {
      const token = localStorage.getItem('token');
      const jobTitle = selectedJobForBids?.title || bid.job_title || 'Job Inquiry';
      
      const response = await axios.post(
        `${API}/bids/${bid.id}/inquiry`,
        { message: `Hi! I have a question about your bid for "${jobTitle}".` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.inquiry_id) {
        navigate(`/inquiries?conversation=${response.data.inquiry_id}`);
      } else {
        navigate(`/inquiries`);
      }
    } catch (error) {
      console.error('Error creating inquiry:', error);
      navigate(`/inquiries?provider=${bid.provider_id}`);
    }
  };

  const handleCloseBidsModal = () => {
    setSelectedJobForBids(null);
    setJobBids([]);
  };

  const handleMessageClient = (bid) => {
    setSelectedJobConversation(bid);
    setShowJobChatModal(true);
  };

  const handleMessageClientFromBid = async (bid) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/bids/${bid.id}/inquiry`,
        { message: `Hi! I'm following up on my bid for "${bid.job_title}".` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.inquiry_id) {
        navigate(`/inquiries?conversation=${response.data.inquiry_id}`);
      } else {
        navigate(`/inquiries`);
      }
    } catch (error) {
      console.error('Error creating inquiry:', error);
      navigate(`/inquiries?client=${bid.client_id}`);
    }
  };

  const handleViewJobDetails = async (bid) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/jobs/${bid.job_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedJobDetails({
        ...response.data,
        type: 'job_bid',
        bid_id: bid.id,
        bid_amount: bid.bid_amount
      });
      setShowJobDetailsModal(true);
    } catch (error) {
      console.error('Error fetching job details:', error);
      alert('Failed to load job details. The job may have been removed.');
    }
  };

  // ==================== Booking Handlers ====================

  const handleCancelBooking = async (booking) => {
    // `booking` may be a full object (from the card) or a raw id (legacy callers).
    const b = typeof booking === 'object' ? booking : bookings.find((x) => x.id === booking);
    const bookingId = typeof booking === 'object' ? booking.id : booking;

    // Price-adjusted, accepted & unpaid → open the cancel-with-note modal so the
    // client can decline a cost that was changed without their agreement.
    if (b && b.status === 'accepted' && b.price_adjusted && b.payment_status !== 'paid') {
      setCancelTarget(b);
      return;
    }

    if (!window.confirm(t('myBookings.confirmCancel'))) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      fetchData();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleConfirmCancelWithNote = async (note) => {
    if (!cancelTarget) return;
    setSubmittingCancel(true);
    try {
      const params = new URLSearchParams();
      if (note) params.append('reason', note);
      const qs = params.toString();
      await axios.delete(`${API}/bookings/${cancelTarget.id}${qs ? `?${qs}` : ''}`);
      setCancelTarget(null);
      fetchData();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setSubmittingCancel(false);
    }
  };

  const handleRejectBooking = (booking) => {
    setRejectTarget(booking);
  };

  const handleConfirmReject = async (reason) => {
    if (!rejectTarget) return;
    setSubmittingReject(true);
    try {
      await axios.put(`${API}/bookings/${rejectTarget.id}/reject?reason=${encodeURIComponent(reason)}`);
      setRejectTarget(null);
      fetchData();
    } catch (error) {
      console.error('Error rejecting booking:', error);
      alert(error.response?.data?.detail || 'Failed to reject booking');
    } finally {
      setSubmittingReject(false);
    }
  };

  const handleMessageProvider = async (booking) => {
    if (!booking?.provider_id) { navigate('/inquiries'); return; }
    try {
      const res = await axios.get(`${API}/inquiries/find?provider_id=${booking.provider_id}&client_id=${user?.id}`);
      if (res.data?.inquiry?.id) {
        navigate(`/inquiries?conversation=${res.data.inquiry.id}`);
        return;
      }
    } catch (e) { /* fall through to create */ }
    try {
      const code = booking.booking_order_code ? ` (#SE-${booking.booking_order_code})` : '';
      const res = await axios.post(`${API}/providers/${booking.provider_id}/message`, {
        message: `Hi! I'd like to discuss my booking for "${booking.service_title}"${code}.`,
      });
      navigate(`/inquiries?conversation=${res.data.inquiry_id}`);
    } catch (e) {
      navigate('/inquiries');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm(t('myBookings.confirmDeleteBooking'))) return;
    
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  const handleHideBooking = async (bookingId) => {
    if (!window.confirm(t('myBookings.confirmHideBooking'))) return;
    
    try {
      await axios.post(`${API}/bookings/${bookingId}/hide`);
      fetchData();
    } catch (error) {
      console.error('Error hiding booking:', error);
      alert(error.response?.data?.detail || 'Failed to hide booking');
    }
  };

  const handleAcceptBooking = (booking) => {
    setSelectedBookingForAccept(booking);
    setQuoteAmount(booking.price || '');
    setQuoteNotes('');
    setProjectEndDate('');
    setProjectEndTime('17:00');
    setBlockCalendar(false);
    setShowAcceptQuoteModal(true);
  };

  const handleAcceptWithQuote = async () => {
    if (!selectedBookingForAccept || !quoteAmount || parseFloat(quoteAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // For project bookings, require end date
    if (selectedBookingForAccept.price_unit === 'project' && !projectEndDate) {
      alert('Please set the project end date');
      return;
    }

    setSubmittingQuote(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('final_amount', quoteAmount);
      if (quoteNotes) params.append('notes', quoteNotes);
      
      // Add project timeline params for project bookings
      if (selectedBookingForAccept.price_unit === 'project') {
        params.append('project_end_date', projectEndDate);
        if (projectEndTime) params.append('project_end_time', projectEndTime);
        params.append('block_calendar', blockCalendar);
      }

      await axios.put(`${API}/bookings/${selectedBookingForAccept.id}/accept-with-quote?${params.toString()}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowAcceptQuoteModal(false);
      setSelectedBookingForAccept(null);
      setQuoteAmount('');
      setQuoteNotes('');
      setProjectEndDate('');
      setProjectEndTime('17:00');
      setBlockCalendar(false);
      fetchData();
      alert(t('myBookings.bookingAccepted'));
    } catch (error) {
      console.error('Error accepting booking:', error);
      alert(error.response?.data?.detail || 'Failed to accept booking');
    } finally {
      setSubmittingQuote(false);
    }
  };

  const calculateFees = (amount) => {
    const parsed = parseFloat(amount) || 0;
    const providerCalc = calculateProviderPayout(parsed);
    return { 
      platformFee: providerCalc.providerFee.toFixed(2), 
      providerPayout: providerCalc.providerPayout.toFixed(2) 
    };
  };

  // ==================== Render ====================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-600">{t('myBookings.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="my-bookings-page">
      <Navbar user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="my-bookings-title">
              {isClient ? t('myBookings.titleClient') : t('myBookings.titleProvider')}
            </h1>
            <p className="text-gray-600 mt-1">
              {isClient 
                ? t('myBookings.subClient')
                : t('myBookings.subProvider')}
            </p>
          </div>
          {isClient && (
            <Link
              to="/create-job"
              className="bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition flex items-center gap-2"
              data-testid="post-new-job-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('myBookings.postNewJob')}
            </Link>
          )}
        </div>

        {/* Main Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200">
          {isProvider && (
            <button
              onClick={() => setActiveTab('my-bids')}
              className={`pb-3 px-1 font-semibold transition border-b-2 ${
                activeTab === 'my-bids'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid="tab-my-bids"
            >
              {t('myBookings.myBids')}
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                {myBids.length}
              </span>
            </button>
          )}
          <button
            onClick={() => setActiveTab('bookings')}
            className={`pb-3 px-1 font-semibold transition border-b-2 relative ${
              activeTab === 'bookings'
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
            data-testid="tab-bookings"
          >
            {isClient ? t('myBookings.bookedJobs') : t('myBookings.myBookings')}
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
              {bookings.length}
            </span>
            {activeDisputes > 0 && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" data-testid="bookings-tab-dispute-dot" />
            )}
          </button>
          {isClient && (
            <button
              onClick={() => setActiveTab('posted-jobs')}
              className={`pb-3 px-1 font-semibold transition border-b-2 ${
                activeTab === 'posted-jobs'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid="tab-posted-jobs"
            >
              {t('myBookings.postedJobs')}
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                {jobs.length}
              </span>
            </button>
          )}
        </div>

        {/* Bookings Tab Content */}
        {activeTab === 'bookings' && (
          <BookingsListView
            bookings={bookings}
            bookingFilter={bookingFilter}
            setBookingFilter={setBookingFilter}
            effectiveRole={effectiveRole}
            onViewDetails={setSelectedBooking}
            onAcceptBooking={handleAcceptBooking}
            onRejectBooking={handleRejectBooking}
            onCancelBooking={handleCancelBooking}
            onMessageProvider={handleMessageProvider}
            onDeleteBooking={handleDeleteBooking}
            onHideBooking={handleHideBooking}
            onRefresh={fetchData}
            formatBookingDate={formatBookingDate}
            formatBookingTime={formatBookingTime}
            getStatusColor={getStatusColor}
          />
        )}

        {/* Posted Jobs Tab Content (Client only) */}
        {isClient && activeTab === 'posted-jobs' && (
          <div>
            {jobs.length > 0 ? (
              <JobsList
                jobs={jobs}
                onViewBids={fetchJobBids}
                onRefresh={fetchData}
              />
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('myBookings.noJobs')}</h3>
                <p className="text-gray-500 mb-6">{t('myBookings.noJobsHint')}</p>
                <Link
                  to="/create-job"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('myBookings.postFirstJob')}
                </Link>
              </div>
            )}
          </div>
        )}

        {/* My Bids Tab Content (Provider only) */}
        {isProvider && activeTab === 'my-bids' && (
          <ProviderBidsTab
            myBids={myBids}
            onWithdrawBid={handleWithdrawBid}
            onDeleteBid={handleDeleteRejectedBid}
            onClearAllBids={handleClearAllBids}
            onViewJobDetails={handleViewJobDetails}
            onMessageClient={handleMessageClientFromBid}
            getStatusColor={getStatusColor}
          />
        )}
      </div>

      {/* Booking Details Modal / Completion Flow */}
      {selectedBooking && (
        (selectedBooking.status === 'completion_requested' || 
         (effectiveRole === 'provider' && ['accepted', 'in_progress'].includes(selectedBooking.status))) ? (
          <BookingCompletionFlow
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
            onUpdate={() => {
              setSelectedBooking(null);
              fetchData();
            }}
            userRole={effectiveRole}
          />
        ) : (
          <SimpleBookingDetailsModal
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
            effectiveRole={effectiveRole}
          />
        )
      )}

      {/* Accept Quote Modal */}
      <AcceptQuoteModal
        booking={selectedBookingForAccept}
        quoteAmount={quoteAmount}
        quoteNotes={quoteNotes}
        projectEndDate={projectEndDate}
        projectEndTime={projectEndTime}
        blockCalendar={blockCalendar}
        submitting={submittingQuote}
        onQuoteAmountChange={setQuoteAmount}
        onQuoteNotesChange={setQuoteNotes}
        onProjectEndDateChange={setProjectEndDate}
        onProjectEndTimeChange={setProjectEndTime}
        onBlockCalendarChange={setBlockCalendar}
        onSubmit={handleAcceptWithQuote}
        onClose={() => {
          setShowAcceptQuoteModal(false);
          setSelectedBookingForAccept(null);
          setQuoteAmount('');
          setQuoteNotes('');
          setProjectEndDate('');
          setProjectEndTime('17:00');
          setBlockCalendar(false);
        }}
        calculateFees={calculateFees}
      />

      {/* Provider: reject a pending booking with a required reason */}
      <BookingReasonModal
        open={!!rejectTarget}
        title="Reject Booking"
        description={rejectTarget ? `Let ${rejectTarget.client_name || 'the client'} know why you can't take "${rejectTarget.service_title}".` : ''}
        confirmLabel="Reject Booking"
        confirmColor="red"
        presets={[
          'Not available on that date/time',
          'Location too far',
          'Price / budget too low',
          'Unable to fulfill this service',
          'Other',
        ]}
        submitting={submittingReject}
        onConfirm={handleConfirmReject}
        onClose={() => setRejectTarget(null)}
      />

      {/* Client: cancel a price-adjusted booking with an optional note */}
      <BookingReasonModal
        open={!!cancelTarget}
        title="Cancel Booking"
        description={cancelTarget ? `The provider changed the price to ₱${Number(cancelTarget.final_amount || 0).toLocaleString()} (originally ₱${Number(cancelTarget.original_price || 0).toLocaleString()}). Cancel if you didn't agree.` : ''}
        confirmLabel="Cancel Booking"
        confirmColor="red"
        presets={null}
        requireText={false}
        submitting={submittingCancel}
        onConfirm={handleConfirmCancelWithNote}
        onClose={() => setCancelTarget(null)}
      />

      {/* Job Details Modal (for My Bids) */}
      {showJobDetailsModal && selectedJobDetails && (
        <JobDetailsModal
          job={selectedJobDetails}
          onClose={() => {
            setShowJobDetailsModal(false);
            setSelectedJobDetails(null);
          }}
          getStatusColor={getStatusColor}
        />
      )}

      {/* Job Chat Modal */}
      {showJobChatModal && selectedJobConversation && (
        <JobChatModal
          isOpen={showJobChatModal}
          onClose={() => {
            setShowJobChatModal(false);
            setSelectedJobConversation(null);
          }}
          job={selectedJobConversation}
          userId={user?.id}
        />
      )}

      {/* Job Bids Modal */}
      {selectedJobForBids && (
        <JobBidsModal
          job={selectedJobForBids}
          bids={jobBids}
          loading={loadingBids}
          processingBid={processingBid}
          onClose={handleCloseBidsModal}
          onAcceptBid={handleAcceptBid}
          onRejectBid={handleRejectBid}
          onMessageProvider={handleMessageProviderFromBid}
          onAskQuestion={handleAskQuestion}
        />
      )}

      <Footer />
    </div>
  );
};

export default MyBookings;
